import {
    NursingAllowanceFollowUpInput,
} from '@libs/types/src/services/input-type/cssz/nursing-allowance-follow-up.input';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import {
    NursingAllowanceCreateInput, NursingAllowanceCreationInput, NursingAllowanceGenerateDecisionNumberInput,
    NursingAllowanceCorrectionInput,
} from '@libs/types/src/services/input-type/cssz/nursing-allowance-create.input';
import { NursingAllowanceFormObject } from '@libs/types/src/services/object-type/folder/nursing-allowance-form.object';
import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    NursingAllowanceSearchInput, NursingAllowanceDecisionInput, NursingAllowanceTakeOverInput,
} from '@libs/types/src/services/input-type/cssz/nursing-allowance-take-over.input';
import {
    NursingAllowanceSearchObject, NursingAllowanceTakeOverObject,
} from '@libs/types/src/services/object-type/folder/nursing-allowance-take-over.object';
import {
    FolderNursingAllowanceGridObject, NursingAllowanceSyncObject,
} from '@libs/types/src/services/object-type/folder/folder-nursing-allowance.object';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from './types/filter.types';

@Injectable({ providedIn: 'root' })
export class FolderNursingAllowanceGqlService extends GqlServiceV2 {

    public getNursingAllowances(input: GridInput | null, fragment: string) {
        return this.query<FolderNursingAllowanceGridObject>(
            `
                query($input: GridInput) {
                    folderNursingAllowance {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public searchNursingAllowances(input: NursingAllowanceSearchInput, fragment: string) {
        return this.query<NursingAllowanceSearchObject>(
            `
                query($input: NursingAllowanceSearchInput!) {
                    folderNursingAllowance {
                        search(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public takeOverNursingAllowance(input: NursingAllowanceTakeOverInput, fragment: string) {
        return this.mutate<NursingAllowanceTakeOverObject>(
            `
                mutation($input: NursingAllowanceTakeOverInput!) {
                    folderNursingAllowance {
                        takeOver(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public refreshNursingAllowanceTakeOver(input: NursingAllowanceDecisionInput, fragment: string) {
        return this.mutate<NursingAllowanceTakeOverObject>(
            `
                mutation($input: NursingAllowanceDecisionInput!) {
                    folderNursingAllowance {
                        refreshTakeOver(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public prepareNursingAllowance(input: IdInput, fragment: string) {
        return this.query<NursingAllowanceFormObject>(
            `
                query($input: IdInput!) {
                    folderNursingAllowance {
                        prepareCreate(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createNursingAllowance(input: NursingAllowanceCreateInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: NursingAllowanceCreateInput!) {
                    folderNursingAllowance {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getNursingAllowanceCreation(input: NursingAllowanceCreationInput, fragment: string) {
        return this.query<NursingAllowanceFormObject | null>(
            `
                query($input: NursingAllowanceCreationInput!) {
                    folderNursingAllowance {
                        creation(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getNursingAllowanceDetail(input: IdInput, fragment: string) {
        return this.query<NursingAllowanceFormObject>(
            `
                query($input: IdInput!) {
                    folderNursingAllowance {
                        detail(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public refreshNursingAllowance(input: IdInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: IdInput!) {
                    folderNursingAllowance {
                        refresh(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }


    public confirmNursingAllowanceDuration(input: NursingAllowanceFollowUpInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: NursingAllowanceFollowUpInput!) {
                    folderNursingAllowance {
                        confirmDuration(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public endNursingAllowanceCare(input: NursingAllowanceFollowUpInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: NursingAllowanceFollowUpInput!) {
                    folderNursingAllowance {
                        endCare(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public generateNursingAllowanceDecisionNumber(input: NursingAllowanceGenerateDecisionNumberInput) {
        return this.mutate<string>(
            `
                mutation($input: NursingAllowanceGenerateDecisionNumberInput!) {
                    folderNursingAllowance {
                        generateDecisionNumber(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public syncNursingAllowances(input: IdInput) {
        return this.mutate<NursingAllowanceSyncObject>(
            `
                mutation($input: IdInput!) {
                    folderNursingAllowance {
                        sync(input: $input) {
                            synchronizedCount
                            issues {
                                decisionNumber
                                message
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public reportNursingAllowanceChanges(input: NursingAllowanceFollowUpInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: NursingAllowanceFollowUpInput!) {
                    folderNursingAllowance {
                        reportChanges(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public correctNursingAllowance(input: NursingAllowanceCorrectionInput, fragment: string) {
        return this.mutate<NursingAllowanceFormObject>(
            `
                mutation($input: NursingAllowanceCorrectionInput!) {
                    folderNursingAllowance {
                        correct(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
